import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewChild, forwardRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { Checkout } from '@df/checkout/checkout.interfaces';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { IZippyChangeEvent, ZippyEventType, subject } from '@df/legacy/ui/zippy/zippy.abstract';
import { ZippyModule } from '@df/legacy/ui/zippy/zippy.module';
import { Giftcards } from '@df/module-giftcards/giftcards';
import { LoqateService } from '@df/module-loqate/loqate.service';
import { MentionMePlace } from '@df/module-mention-me/mention-me.interfaces';
import { IfNotStandaloneDirective } from '@df/module-pwa/utils/if-not-standalone.directive';
import { ADDRESS_FIELDS, IAddressBasicData } from '@df/session/api/address-book/address';
import { BodyClassService } from '@df/ui/body-class.service';
import { ClickEventDirective } from '@df/ui/common/click-event.directive';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { CouponFormComponent } from '../basket/coupon-form.component';
import { BasketViewTotalsComponent } from '../basket/totals/basket-view-totals.component';
import { BraintreePaypalCheckoutComponent } from '../module/braintree/paypal/braintree-paypal-checkout.component';
import { GiftcardsFormComponent } from '../module/giftcards/giftcards-form.component';
import { MentionMeWrapperComponent } from '../module/mention-me/mention-me-wrapper.component';
import { PaypalExpressButtonComponent } from '../module/paypal-express/paypal-express-button.component';
import { LocalPwaIntegrationService } from '../module/pwa/pwa-integration.service.decorator';
import { StripeExpressCheckoutComponent } from '../module/stripe/stripe-express-checkout.component';
import { StripeExpressPaymentRequestButtonComponent } from '../module/stripe/stripe-express-payment-request-button.component';
import { StripePaymentRequestButtonComponent } from '../module/stripe/stripe-payment-request-button.component';
import { IfStoreDirective } from '../multisite/if-store.directive';
import { IfNotTradeStoreDirective } from '../multisite/trade/if-not-trade-store.directive';
import { IfTradeStoreDirective } from '../multisite/trade/if-trade-store.directive';
import { ActionComponent } from '../ui/api/action.component';
import { ResultComponent } from '../ui/resut/result.component';
import { NAddressValidationModal } from './address-validation-modal/address-validation-modal.component';
import { CheckoutAccountComponent } from './checkout-account.component';
import { CheckoutAddDonationComponent } from './checkout-add-donation.component';
import { CheckoutAddresssComponent } from './checkout-address.component';
import { CheckoutConfirmationComponent } from './checkout-confirmation.component';
import { CheckoutMinibasketComponent } from './checkout-minibasket.component';
import { CheckoutPaymentMethodsComponent } from './checkout-payment-methods.component';
import { CheckoutShippingMethodsComponent } from './checkout-shipping-methods.component';
import { CheckoutStepComponent } from './checkout-step.component';
import { CheckoutStorePickupComponent } from './checkout-store-pickup.component';
export const CHECKOUT_SUBMIT_CLICK_EVENT_NAME = 'checkout.submit-button.click';

declare module '@df/checkout/checkout.interfaces' {
    namespace Checkout {
        interface IShippingMethod {
            surcharge?: string;
        }

        export enum EStepName {
            deliveryOptions = 'deliveryOptions'
        }

        interface IQuoteDataCustom {
            is_pwa_order?: 1 | 0;
        }
    }
}

Object.assign(Checkout.EStepName, {
    deliveryOptions: 'deliveryOptions'
});

@Component({
    selector: 'checkout-form',
    templateUrl: './checkout-form.html',
    styles: [
        `
            .checkout-minibasket-hide-up {
                .hide-up.ng-hide {
                    margin-top: calc(var(--element-height-checkout-minibasket) * -1);
                }
            }
        `
    ],
    standalone: true,
    imports: [
        ActionComponent,
        forwardRef(() => BasketViewTotalsComponent),
        BraintreePaypalCheckoutComponent,
        ClickEventDirective,
        CheckoutAccountComponent,
        CheckoutAddDonationComponent,
        CheckoutAddresssComponent,
        CheckoutAddresssComponent,
        CheckoutConfirmationComponent,
        CheckoutMinibasketComponent,
        CheckoutPaymentMethodsComponent,
        CheckoutShippingMethodsComponent,
        CheckoutStepComponent,
        CheckoutStorePickupComponent,
        CouponFormComponent,
        ElementHeightDirective,
        FormsModule,
        GiftcardsFormComponent,
        IfNotTradeStoreDirective,
        IfStoreDirective,
        IfTradeStoreDirective,
        MentionMeWrapperComponent,
        NgClass,
        NgClass,
        PaypalExpressButtonComponent,
        PriceComponent,
        ResultComponent,
        ResultComponent,
        RouterLink,
        StripeExpressPaymentRequestButtonComponent,
        StripePaymentRequestButtonComponent,
        IfNotStandaloneDirective,
        TranslationModule,
        ZippyModule,
        StripeExpressCheckoutComponent
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: CheckoutFormComponentDef, useExisting: CheckoutFormComponent },
        { provide: Checkout.CheckoutFormPaymentComponentToken, useExisting: CheckoutFormComponent }
    ]
})
export class CheckoutFormComponent extends CheckoutFormComponentDef {
    submitClickEventName = CHECKOUT_SUBMIT_CLICK_EVENT_NAME;
    private bodyClassService = inject(BodyClassService);
    private loqateService = inject(LoqateService);
    private dfModalService = inject(DfModalService);
    private pwaIntegrationService = inject(LocalPwaIntegrationService);

    @ViewChild('shippingAddress', { read: CheckoutAddresssComponent })
    private shippingAddressEl?: CheckoutAddresssComponent;

    CHECKOUT_BODY_CLASS = 'bg-col-4';
    protected readonly backorderNode =
        'Your bag includes items that are on Back Order. You will only have the option to pay via ProForma or Pay on Account (which ever is relevant to your account). The bag total will include all in stock and back order items. You will not be charged or asked to pay for any Back Order items until they come into stock.';

    protected readonly EStepName = Checkout.EStepName;
    protected readonly MentionMePlace = MentionMePlace;

    override ngOnInit() {
        super.ngOnInit();
        this.subscriptions.add(
            this.events.on(Checkout.EVENTS.PickShippingMethod).subscribe(() => {
                this.detectChanges();
            })
        );
        this.bodyClassService.add(this.CHECKOUT_BODY_CLASS, this);
        this.saveCustomPwaSource();
    }
    override ngOnDestroy() {
        super.ngOnDestroy();
        this.bodyClassService.remove(this.CHECKOUT_BODY_CLASS, this);
    }

    ifDotActive(index: number): boolean {
        return index < this.currentStepIndex;
    }

    private saveCustomPwaSource(): void {
        if (!!this.data.custom?.is_pwa_order !== this.pwaIntegrationService.isStandalone) {
            this.data.custom = {
                ...(this.data.custom || {}),
                is_pwa_order: this.pwaIntegrationService.isStandalone ? 1 : 0
            };
            this.saveCustomData();
        }
    }

    override goToNextStep() {
        const lastStep: number = this.currentStepIndex;
        super.goToNextStep();
        this.closeAccordions(lastStep);
    }
    goTo(step) {
        const lastStep: number = this.currentStepIndex;
        step.goTo();
        this.closeAccordions(lastStep, true);
    }
    closeAccordions(lastStep: number, scrollToTop = false) {
        if (this.currentStepIndex !== lastStep) {
            this.closeBasketAccordion();
            if (this.currentStepName !== 'account') {
                this.closeCustomerAccordion();
            }
        } else if (scrollToTop) {
            if (this.currentStep) {
                const element = document
                    .querySelector('#checkout-step-' + this.currentStep.name)
                    ?.querySelector('.input') as HTMLInputElement;
                if (element) {
                    this.scrollToService.scrollTo(element);
                }
            }
        }
    }
    closeDeliveryAccoridon() {
        this.closeAccordion('delivery', 'deliveryTab');
    }
    closeCustomerAccordion() {
        this.closeAccordion('customer', 'customerTab');
    }
    closeBasketAccordion() {
        this.closeAccordion('basket', 'basketTab');
    }
    closeDeliveryOptionsAccoridon() {
        this.closeAccordion('deliveryOptions', 'deliveryOptionsTab');
    }
    private closeAccordion(name: string, group: string): void {
        subject.next({
            id: {
                name,
                group,
                isGroup: false
            },
            type: ZippyEventType.close
        });

        subject.subscribe(event => {
            if (
                event.id.name === 'delivery' &&
                event.id.group === 'deliveryTab' &&
                event.type === ZippyEventType.change &&
                (event as IZippyChangeEvent).visible
            ) {
                this.goToStep('delivery');
            }
        });
        this.detectChanges();
    }

    trackByGiftcard(index: number, giftcard: Giftcards.IAddedGiftcardData): string {
        return giftcard.code;
    }
    get showDonation(): boolean {
        if (this.data.payment.method === undefined) {
            return false;
        }
        return true;
    }
    get canShowBackorderNote(): boolean {
        return !!this.app.customer.basket.items.find(item => item.isBackorderOnly());
    }

    override getSubmissionData(): Checkout.ISubmissionData {
        const submissionData = super.getSubmissionData();

        // Add PWA flag to custom data when app is standalone

        if (this.pwaIntegrationService.isStandalone) {
            const custom = submissionData.custom || {};
            submissionData.custom = {
                ...custom,
                is_pwa_order: 1
            };
        }
        return submissionData;
    }

    override submit(): void {
        if (this.getSubmissionData().paymentMethod?.method || this.isFree) {
            super.submit();
        } else {
            this.status.error = 'Please select a payment method.';
            this.timeoutService.setTimeout(() => {
                this.status.reset();
            }, 2000);
        }
    }

    async confirmDelivery() {
        if (this.currentStep?.name !== Checkout.EStepName.delivery) {
            this.closeDeliveryAccoridon();
            return;
        }
        if (this.saveShippingAddressStatus.busy) {
            return;
        }
        if (!this.data.address.shipping.customer_address_id && this.loqateService.isEnabled) {
            if (this.steps['delivery'].valid) {
                this.saveShippingAddressStatus.busy = true;
                try {
                    const validationReult = await this.loqateService.validateAddress(this.data.address.shipping);
                    if (!validationReult.valid) {
                        this.dfModalService.open(undefined, NAddressValidationModal.modalId, undefined, <
                            NAddressValidationModal.IModalData
                        >{
                            address: this.data.address.shipping,
                            addressValidationResult: validationReult,
                            onConfirm: (address: IAddressBasicData | boolean) => {
                                switch (address) {
                                    case true:
                                        break;
                                    case false:
                                        this.goToStep('delivery');
                                        break;
                                    default:
                                        ADDRESS_FIELDS.forEach(field => {
                                            this.data.address.shipping[field] = address[field];
                                        });
                                        this._saveShippingAddress(false, () => {
                                            this.shippingAddressEl?.detectChanges();
                                        });
                                }
                            }
                        });
                    }
                } catch (err) {
                    this.app.logger.error(err);
                }
                this.saveShippingAddressStatus.busy = false;
            }
        }
        this.goToNextStep();
    }

    protected confirmDeliveryOptions() {
        if (this.currentStep?.name !== Checkout.EStepName.deliveryOptions) {
            this.closeDeliveryOptionsAccoridon();
            return;
        }
        this.goToNextStep();
    }
}
